import { BaseFieldFilter, NativeFieldFilter } from 'api/dashboard/widget';
import { MultiSelectField } from 'components/organisms/tasks/fields';
import { MultiSelectFieldFilter } from 'components/organisms/tasks/filters/multi-select';
import { Operation } from 'components/organisms/tasks/filters/operations';
import { SelectFilterOperationType } from 'components/organisms/tasks/filters/select';
import { unreachable } from 'utils/unreachable';
import { fromApiValue, toType } from './select';

const toApiFormat = (filter: MultiSelectFieldFilter): BaseFieldFilter => {
  if (filter.field.fieldName === 'currentPhases') {
    // Encode current phases values
    const transformValues = (values: any[]) => {
      return values.map((name) => btoa(name));
    };

    switch (filter.type) {
      case SelectFilterOperationType.IsEmpty:
        return {
          operation: Operation.IsEmpty,
          sourceType: filter.field.sourceType,
        };
      case SelectFilterOperationType.IsNotEmpty:
        return {
          operation: Operation.IsNotEmpty,
          sourceType: filter.field.sourceType,
        };
      case SelectFilterOperationType.Is:
        return {
          operation: Operation.Value,
          value: transformValues(filter.value ?? []).join(','),
          sourceType: filter.field.sourceType,
        };
      case SelectFilterOperationType.IsNot:
        return {
          operation: Operation.IsNot,
          value: transformValues(filter.value ?? []).join(','),
          sourceType: filter.field.sourceType,
        };
      case SelectFilterOperationType.IsNoneOf:
        return {
          operation: Operation.NoneOf,
          value: transformValues(filter.value ?? []).join(','),
          sourceType: filter.field.sourceType,
        };
      case SelectFilterOperationType.IsOneOf:
        return {
          operation: Operation.OneOf,
          value: transformValues(filter.value ?? []).join(','),
          sourceType: filter.field.sourceType,
        };
      default:
        return unreachable(filter);
    }
  }

  // Default behavior for other multi-select fields
  switch (filter.type) {
    case SelectFilterOperationType.IsEmpty:
      return {
        operation: Operation.IsEmpty,
        sourceType: filter.field.sourceType,
      };
    case SelectFilterOperationType.IsNotEmpty:
      return {
        operation: Operation.IsNotEmpty,
        sourceType: filter.field.sourceType,
      };
    case SelectFilterOperationType.Is:
      return {
        operation: Operation.Value,
        value: (filter.value ?? []).join(','),
        sourceType: filter.field.sourceType,
      };
    case SelectFilterOperationType.IsNot:
      return {
        operation: Operation.IsNot,
        value: (filter.value ?? []).join(','),
        sourceType: filter.field.sourceType,
      };
    case SelectFilterOperationType.IsNoneOf:
      return {
        operation: Operation.NoneOf,
        value: (filter.value ?? []).join(','),
        sourceType: filter.field.sourceType,
      };
    case SelectFilterOperationType.IsOneOf:
      return {
        operation: Operation.OneOf,
        value: (filter.value ?? []).join(','),
        sourceType: filter.field.sourceType,
      };
    default:
      return unreachable(filter);
  }
};

export const parseEncodedValue = (input: string): number[] => {
  if (!input || input === '') {
    return [];
  }

  const base64Values = input.split(',');

  return base64Values.map((base64Value) => {
    try {
      const decodedString = atob(base64Value.trim());

      const num = Number(decodedString);

      if (Number.isNaN(num)) {
        return decodedString as unknown as number;
      }

      return num;
    } catch (error) {
      const num = Number(base64Value);

      return Number.isNaN(num) ? (base64Value as unknown as number) : num;
    }
  });
};

const parse = (
  { value, operation }: NativeFieldFilter,
  field: MultiSelectField,
): MultiSelectFieldFilter => {
  const type = toType(operation);

  switch (type) {
    case SelectFilterOperationType.IsEmpty:
    case SelectFilterOperationType.IsNotEmpty:
      return { field, type };
    case SelectFilterOperationType.Is:
    case SelectFilterOperationType.IsNot:
      if (field.fieldName === 'currentPhases') {
        return { field, type, value: parseEncodedValue(value ?? '') };
      }

      return { field, type, value: fromApiValue(value ?? '') };
    case SelectFilterOperationType.IsOneOf:
    case SelectFilterOperationType.IsNoneOf:
      if (field.fieldName === 'currentPhases') {
        return { field, type, value: parseEncodedValue(value ?? '') };
      }

      return { field, type, value: fromApiValue(value ?? '') };
    case SelectFilterOperationType.IsAtLeast:
    case SelectFilterOperationType.IsAtMost:
    default:
      return unreachable(
        `${type} cannot be parsed for ${field.type} field` as never,
      );
  }
};

export const MultiSelectFilter = {
  parse,
  toApiFormat,
  from: fromApiValue,
};
